import React, { useState, useContext } from 'react';
import AuthContext from '../context/AuthContext';
import { Box, TextField, Button, Typography, IconButton, InputAdornment, Link, Alert, CircularProgress, Checkbox, FormControlLabel } from '@mui/material';
import { Visibility, VisibilityOff, ArrowBack, CheckCircleOutline, Shop } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { elUpperCase } from '../utils/text';
import { NAV_BG, AMBER, AMBER_DARK } from '../theme';

const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

const NAV_MID = '#163571';
const GREEN = '#10b981';

const BusIcon = ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <rect x="2" y="6" width="20" height="12" rx="2.5" fill={NAV_BG} />
        <rect x="4" y="9" width="4" height="3" rx="1" fill={AMBER} opacity="0.85" />
        <rect x="10" y="9" width="4" height="3" rx="1" fill={AMBER} opacity="0.85" />
        <rect x="16" y="9" width="3" height="3" rx="1" fill={AMBER} opacity="0.85" />
        <line x1="2" y1="14" x2="22" y2="14" stroke="rgba(255,255,255,0.15)" strokeWidth="0.8" />
        <circle cx="7" cy="20" r="2" fill={NAV_BG} />
        <circle cx="7" cy="20" r="1" fill={AMBER} />
        <circle cx="17" cy="20" r="2" fill={NAV_BG} />
        <circle cx="17" cy="20" r="1" fill={AMBER} />
    </svg>
);

const RouteMap = () => (
    <svg viewBox="0 0 460 300" fill="none" xmlns="http://www.w3.org/2000/svg"
        style={{ width: '100%', maxWidth: 460, filter: 'drop-shadow(0 24px 48px rgba(0,0,0,0.35))' }}>

        {/* Card background */}
        <rect width="460" height="300" rx="18" fill="rgba(255,255,255,0.04)" />
        <rect width="460" height="300" rx="18" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="1" />

        {/* Grid roads */}
        {[70, 155, 240].map(y => (
            <line key={y} x1="20" y1={y} x2="440" y2={y}
                stroke="rgba(255,255,255,0.06)" strokeWidth="7" strokeLinecap="round" />
        ))}
        {[90, 185, 280, 375].map(x => (
            <line key={x} x1={x} y1="20" x2={x} y2="280"
                stroke="rgba(255,255,255,0.06)" strokeWidth="7" strokeLinecap="round" />
        ))}

        {/* Route path glow */}
        <polyline
            points="36,240 90,240 90,155 185,155 185,70 280,70 280,155 375,155 375,240 444,240"
            stroke={AMBER} strokeWidth="10" strokeLinecap="round" strokeLinejoin="round"
            fill="none" opacity="0.12"
        />
        {/* Route path */}
        <polyline
            points="36,240 90,240 90,155 185,155 185,70 280,70 280,155 375,155 375,240 444,240"
            stroke={AMBER} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
            fill="none" strokeDasharray="8 4" opacity="0.85"
        />

        {/* Defs for animated path */}
        <defs>
            <path id="routePath" d="M36,240 L90,240 L90,155 L185,155 L185,70 L280,70 L280,155 L375,155 L375,240 L444,240" />
        </defs>

        {/* Bus dot 1 */}
        <g>
            <circle r="7" fill={AMBER}>
                <animateMotion dur="5s" repeatCount="indefinite" begin="0s">
                    <mpath href="#routePath" />
                </animateMotion>
            </circle>
            <circle r="13" fill={AMBER} opacity="0.2">
                <animateMotion dur="5s" repeatCount="indefinite" begin="0s">
                    <mpath href="#routePath" />
                </animateMotion>
            </circle>
        </g>

        {/* Bus dot 2 (offset) */}
        <g>
            <circle r="6" fill="#60a5fa">
                <animateMotion dur="5s" repeatCount="indefinite" begin="2.5s">
                    <mpath href="#routePath" />
                </animateMotion>
            </circle>
            <circle r="11" fill="#60a5fa" opacity="0.18">
                <animateMotion dur="5s" repeatCount="indefinite" begin="2.5s">
                    <mpath href="#routePath" />
                </animateMotion>
            </circle>
        </g>

        {/* Stop markers */}
        <circle cx="36" cy="240" r="6" fill={AMBER} opacity="0.9" />
        <circle cx="36" cy="240" r="11" fill="none" stroke={AMBER} strokeWidth="1.5" opacity="0.35" />
        <circle cx="90" cy="155" r="4.5" fill="white" opacity="0.5" />
        <circle cx="185" cy="155" r="4.5" fill="white" opacity="0.5" />
        <circle cx="185" cy="70" r="4.5" fill="white" opacity="0.5" />
        <circle cx="280" cy="155" r="4.5" fill="white" opacity="0.5" />
        <circle cx="375" cy="155" r="4.5" fill="white" opacity="0.5" />

        {/* School destination */}
        <rect x="406" y="222" width="38" height="36" rx="7" fill={AMBER} fillOpacity="0.12" stroke={AMBER} strokeWidth="1.5" strokeOpacity="0.6" />

        {/* Depot marker */}
        <rect x="14" y="252" width="44" height="18" rx="4" fill="rgba(245,158,11,0.12)" />

        {/* Live status badge */}
        <rect x="16" y="16" width="36" height="24" rx="12" fill="rgba(16,185,129,0.15)" stroke="rgba(16,185,129,0.3)" strokeWidth="1" />
        <circle cx="34" cy="28" r="4" fill={GREEN}>
            <animate attributeName="opacity" values="1;0.4;1" dur="1.8s" repeatCount="indefinite" />
        </circle>
    </svg>
);

// ─── Register sub-component ──────────────────────────────────────────────────
const Register = ({ backendUrl, onBack, t }) => {
    // steps: 'role' | 'driver_email' | 'driver_code' | 'driver_password'
    //        | 'parent_info' | 'parent_code' | 'parent_password' | 'done'
    const [step, setStep] = useState('role');
    const [role, setRole] = useState(null); // 'driver' | 'parent'
    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [code, setCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [resendCount, setResendCount] = useState(0);
    const MAX_DRIVER_RESENDS = 3;
    const [termsAccepted, setTermsAccepted] = useState(false);

    const regInputSx = {
        mb: 2,
        '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            fontFamily: '"Outfit", sans-serif',
            background: 'white',
            '& fieldset': { borderColor: '#e2e8f0' },
            '&:hover fieldset': { borderColor: '#cbd5e1' },
            '&.Mui-focused fieldset': { borderColor: AMBER, borderWidth: 2 },
        },
        '& .MuiInputBase-input': {
            fontFamily: '"Outfit", sans-serif',
            fontSize: '0.95rem',
            color: NAV_BG,
            padding: '12px 14px',
        },
    };

    const labelSx = {
        fontFamily: '"Outfit", sans-serif',
        fontWeight: 600,
        fontSize: '0.8rem',
        color: '#64748b',
        mb: 0.75,
        letterSpacing: '0.04em',
    };

    const primaryBtn = {
        fontFamily: '"Outfit", sans-serif',
        fontWeight: 700,
        fontSize: '0.97rem',
        textTransform: 'none',
        borderRadius: '10px',
        py: 1.45,
        background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`,
        color: NAV_BG,
        boxShadow: `0 6px 24px rgba(245,158,11,0.35)`,
        '&:hover': { background: `linear-gradient(135deg, #fbbf24 0%, ${AMBER} 100%)`, transform: 'translateY(-1px)' },
        '&:active': { transform: 'translateY(0)' },
        '&.Mui-disabled': { background: '#e2e8f0', color: '#94a3b8', boxShadow: 'none' },
    };

    const goBack = () => {
        setError('');
        if (step === 'role') { onBack(); return; }
        if (step === 'driver_email' || step === 'parent_info' || step === 'teacher_email') { setStep('role'); return; }
        if (step === 'driver_code') { setStep('driver_email'); return; }
        if (step === 'driver_password') { setStep('driver_code'); return; }
        if (step === 'teacher_code') { setStep('teacher_email'); return; }
        if (step === 'teacher_password') { setStep('teacher_code'); return; }
        if (step === 'parent_code') { setStep('parent_info'); return; }
        if (step === 'parent_password') { setStep('parent_code'); return; }
    };

    // ── Teacher: start registration ───────────────────────────────────────────
    const handleTeacherStart = async () => {
        if (!isValidEmail(email)) { setError(t('common.email_invalid')); return; }
        setError('');
        setLoading(true);
        try {
            const res = await fetch(backendUrl + 'api/teacher/register/start/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });
            if (res.ok) {
                setResendCount(0);
                setCode('');
                setStep('teacher_code');
            } else {
                const data = await res.json().catch(() => ({}));
                if (res.status === 404) {
                    setError(t('login.reg_teacher_no_account'));
                } else if (res.status === 409) {
                    setError(t('login.reg_teacher_already_active'));
                } else {
                    setError(data.detail || t('login.reg_error_send'));
                }
            }
        } catch {
            setError(t('login.reg_error_send'));
        } finally {
            setLoading(false);
        }
    };

    // ── Teacher: resend code ──────────────────────────────────────────────────
    const handleTeacherResend = async () => {
        if (resendCount >= MAX_DRIVER_RESENDS) return;
        setError('');
        setLoading(true);
        try {
            const res = await fetch(backendUrl + 'api/teacher/register/resend-code/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });
            if (res.ok) {
                setResendCount(c => c + 1);
                setCode('');
            } else if (res.status === 429) {
                setError(t('login.reg_resend_limit'));
            } else {
                setError(t('login.reg_error_send'));
            }
        } catch {
            setError(t('login.reg_error_send'));
        } finally {
            setLoading(false);
        }
    };

    // ── Driver: start registration ────────────────────────────────────────────
    const handleDriverStart = async () => {
        if (!isValidEmail(email)) { setError(t('common.email_invalid')); return; }
        setError('');
        setLoading(true);
        try {
            const res = await fetch(backendUrl + 'api/driver/register/start/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });
            if (res.ok) {
                setResendCount(0);
                setCode('');
                setStep('driver_code');
            } else {
                const data = await res.json().catch(() => ({}));
                if (res.status === 404) {
                    setError(t('login.reg_driver_no_account'));
                } else if (res.status === 409) {
                    setError(t('login.reg_driver_already_active'));
                } else {
                    setError(data.detail || t('login.reg_error_send'));
                }
            }
        } catch {
            setError(t('login.reg_error_send'));
        } finally {
            setLoading(false);
        }
    };

    // ── Driver: resend code ───────────────────────────────────────────────────
    const handleDriverResend = async () => {
        if (resendCount >= MAX_DRIVER_RESENDS) return;
        setError('');
        setLoading(true);
        try {
            const res = await fetch(backendUrl + 'api/driver/register/resend-code/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });
            if (res.ok) {
                setResendCount(c => c + 1);
                setCode('');
            } else if (res.status === 429) {
                setError(t('login.reg_resend_limit'));
            } else {
                setError(t('login.reg_error_send'));
            }
        } catch {
            setError(t('login.reg_error_send'));
        } finally {
            setLoading(false);
        }
    };

    // ── Driver / Teacher: verify code (shared endpoint) ──────────────────────
    const handleDriverVerifyCode = async () => {
        setError('');
        setLoading(true);
        try {
            const res = await fetch(backendUrl + 'api/driver/password-reset/verify/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, code }),
            });
            if (res.ok) {
                setStep(step === 'teacher_code' ? 'teacher_password' : 'driver_password');
            } else {
                setError(t('login.reg_error_invalid_code'));
            }
        } catch {
            setError(t('login.reg_error_invalid_code'));
        } finally {
            setLoading(false);
        }
    };

    // ── Driver: set password ──────────────────────────────────────────────────
    const handleDriverSetPassword = async () => {
        setError('');
        if (newPassword !== confirmPassword) { setError(t('login.reg_error_password_mismatch')); return; }
        if (newPassword.length < 6) { setError(t('login.reg_error_password_short')); return; }
        setLoading(true);
        try {
            const res = await fetch(backendUrl + 'api/driver/password-reset/confirm/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, code, new_password: newPassword }),
            });
            if (res.ok) {
                setStep('done');
            } else {
                setError(t('login.reg_error_invalid_code'));
            }
        } catch {
            setError(t('login.reg_error_invalid_code'));
        } finally {
            setLoading(false);
        }
    };

    // ── Parent: start registration ────────────────────────────────────────────
    const handleParentStart = async () => {
        if (!isValidEmail(email)) { setError(t('common.email_invalid')); return; }
        setError('');
        setLoading(true);
        try {
            const res = await fetch(backendUrl + 'api/parent/register/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, first_name: firstName, last_name: lastName }),
            });
            if (res.ok) {
                setCode('');
                setStep('parent_code');
            } else {
                const data = await res.json().catch(() => ({}));
                setError(data.detail || t('login.reg_error_send'));
            }
        } catch {
            setError(t('login.reg_error_send'));
        } finally {
            setLoading(false);
        }
    };

    // ── Parent: verify OTP ────────────────────────────────────────────────────
    const handleParentVerifyCode = async () => {
        setError('');
        setLoading(true);
        try {
            const res = await fetch(backendUrl + 'api/parent/verify-otp/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, code }),
            });
            if (res.ok) {
                setStep('parent_password');
            } else {
                setError(t('login.reg_error_invalid_code'));
            }
        } catch {
            setError(t('login.reg_error_invalid_code'));
        } finally {
            setLoading(false);
        }
    };

    // ── Parent: set password ──────────────────────────────────────────────────
    const handleParentSetPassword = async () => {
        setError('');
        if (newPassword !== confirmPassword) { setError(t('login.reg_error_password_mismatch')); return; }
        if (newPassword.length < 6) { setError(t('login.reg_error_password_short')); return; }
        setLoading(true);
        try {
            const res = await fetch(backendUrl + 'api/parent/set-password/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, code, new_password: newPassword }),
            });
            if (res.ok) {
                setStep('done');
            } else {
                setError(t('login.reg_error_invalid_code'));
            }
        } catch {
            setError(t('login.reg_error_invalid_code'));
        } finally {
            setLoading(false);
        }
    };

    // ── Step progress helpers ─────────────────────────────────────────────────
    const driverSteps = ['driver_email', 'driver_code', 'driver_password'];
    const teacherSteps = ['teacher_email', 'teacher_code', 'teacher_password'];
    const parentSteps = ['parent_info', 'parent_code', 'parent_password'];
    const currentSteps = role === 'driver' ? driverSteps : role === 'teacher' ? teacherSteps : role === 'parent' ? parentSteps : [];
    const stepIdx = currentSteps.indexOf(step);

    const stepTitle = {
        role: t('login.reg_title'),
        driver_email: t('login.reg_driver_email_title'),
        driver_code: t('login.reg_code_title'),
        driver_password: t('login.reg_password_title'),
        teacher_email: t('login.reg_teacher_email_title'),
        teacher_code: t('login.reg_code_title'),
        teacher_password: t('login.reg_password_title'),
        parent_info: t('login.reg_parent_info_title'),
        parent_code: t('login.reg_code_title'),
        parent_password: t('login.reg_password_title'),
    }[step] || t('login.reg_title');

    // ── Done screen ───────────────────────────────────────────────────────────
    if (step === 'done') {
        return (
            <Box sx={{ textAlign: 'center', py: 2 }}>
                <CheckCircleOutline sx={{ fontSize: 52, color: '#10b981', mb: 2 }} />
                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '1.1rem', color: NAV_BG, mb: 1 }}>
                    {t('login.reg_done_title')}
                </Typography>
                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.88rem', color: '#64748b', mb: 2 }}>
                    {t('login.reg_done_subtitle')}
                </Typography>
                <Button onClick={onBack} variant="text" startIcon={<ArrowBack />}
                    sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, color: AMBER_DARK, textTransform: 'none', mt: 1 }}>
                    {t('login.reg_back_to_login')}
                </Button>
            </Box>
        );
    }

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                {step !== 'role' && (
                    <IconButton onClick={goBack} size="small" sx={{ color: '#64748b', p: 0.5 }}>
                        <ArrowBack sx={{ fontSize: 20 }} />
                    </IconButton>
                )}
                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '1.15rem', color: NAV_BG }}>
                    {stepTitle}
                </Typography>
            </Box>

            {/* Step progress bar (only during typed steps) */}
            {stepIdx >= 0 && (
                <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                    {currentSteps.map((s, i) => (
                        <Box key={s} sx={{
                            height: 3, flex: 1, borderRadius: 2,
                            background: stepIdx >= i ? AMBER : '#e2e8f0',
                            transition: 'background 0.3s',
                        }} />
                    ))}
                </Box>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: '10px', fontFamily: '"Outfit", sans-serif', fontSize: '0.88rem' }}>
                    {error}
                </Alert>
            )}

            {/* ── Role selection ── */}
            {step === 'role' && (
                <Box>
                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.88rem', color: '#64748b', mb: 2.5 }}>
                        {t('login.reg_choose_role')}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {[
                            { key: 'parent', label: t('login.reg_role_parent'), desc: t('login.reg_role_parent_desc') },
                            { key: 'driver', label: t('login.reg_role_driver'), desc: t('login.reg_role_driver_desc') },
                            { key: 'teacher', label: t('login.reg_role_teacher'), desc: t('login.reg_role_teacher_desc') },
                        ].map(({ key, label, desc }) => (
                            <Box key={key}
                                onClick={() => {
                                    setRole(key);
                                    setStep(key === 'driver' ? 'driver_email' : key === 'teacher' ? 'teacher_email' : 'parent_info');
                                    setError('');
                                }}
                                sx={{
                                    border: '1.5px solid #e2e8f0',
                                    borderRadius: '12px',
                                    p: '14px 16px',
                                    cursor: 'pointer',
                                    background: 'white',
                                    transition: 'all 0.18s',
                                    '&:hover': { borderColor: AMBER, boxShadow: `0 0 0 3px rgba(245,158,11,0.1)` },
                                }}>
                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '1rem', color: NAV_BG }}>
                                    {label}
                                </Typography>
                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.8rem', color: '#94a3b8', mt: 0.25 }}>
                                    {desc}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                    <Box sx={{ textAlign: 'center', mt: 3 }}>
                        <Link component="button" type="button" underline="hover"
                            onClick={onBack}
                            sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem', color: '#94a3b8', cursor: 'pointer', '&:hover': { color: AMBER_DARK } }}>
                            {t('login.reg_back_to_login')}
                        </Link>
                    </Box>
                </Box>
            )}

            {/* ── Driver: email ── */}
            {step === 'driver_email' && (
                <Box>
                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.88rem', color: '#64748b', mb: 2.5 }}>
                        {t('login.reg_driver_email_subtitle')}
                    </Typography>
                    <Typography sx={labelSx}>{elUpperCase(t('login.reg_driver_email_label'))}</Typography>
                    <TextField
                        fullWidth autoFocus type="email"
                        placeholder={t('login.reg_driver_email_placeholder')}
                        value={email} onChange={e => setEmail(e.target.value)}
                        size="small" sx={regInputSx}
                        onKeyDown={e => e.key === 'Enter' && email && handleDriverStart()}
                    />
                    <Button fullWidth variant="contained" disableElevation disabled={!email || loading}
                        onClick={handleDriverStart} sx={primaryBtn}>
                        {loading ? <CircularProgress size={18} sx={{ color: NAV_BG }} /> : t('login.reg_next')}
                    </Button>
                </Box>
            )}

            {/* ── Teacher: email ── */}
            {step === 'teacher_email' && (
                <Box>
                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.88rem', color: '#64748b', mb: 2.5 }}>
                        {t('login.reg_teacher_email_subtitle')}
                    </Typography>
                    <Typography sx={labelSx}>{elUpperCase(t('login.reg_driver_email_label'))}</Typography>
                    <TextField
                        fullWidth autoFocus type="email"
                        placeholder={t('login.reg_driver_email_placeholder')}
                        value={email} onChange={e => setEmail(e.target.value)}
                        size="small" sx={regInputSx}
                        onKeyDown={e => e.key === 'Enter' && email && handleTeacherStart()}
                    />
                    <Button fullWidth variant="contained" disableElevation disabled={!email || loading}
                        onClick={handleTeacherStart} sx={primaryBtn}>
                        {loading ? <CircularProgress size={18} sx={{ color: NAV_BG }} /> : t('login.reg_next')}
                    </Button>
                </Box>
            )}

            {/* ── Code verification (shared for driver + teacher + parent) ── */}
            {(step === 'driver_code' || step === 'teacher_code' || step === 'parent_code') && (
                <Box>
                    <Alert severity="info" sx={{ mb: 2.5, borderRadius: '10px', fontFamily: '"Outfit", sans-serif', fontSize: '0.88rem', background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', '& .MuiAlert-icon': { color: '#3b82f6' } }}>
                        {t('login.reg_code_subtitle')}
                    </Alert>
                    <Typography sx={labelSx}>{elUpperCase(t('login.reg_code_label'))}</Typography>
                    <TextField
                        fullWidth autoFocus
                        inputProps={{ maxLength: 6, style: { fontSize: '1.4rem', textAlign: 'center', letterSpacing: '0.25em' } }}
                        placeholder={t('login.reg_code_placeholder')}
                        value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                        size="small" sx={{ ...regInputSx, mb: 3, '& .MuiInputBase-input::placeholder': { letterSpacing: 'normal', fontSize: '0.9rem' } }}
                        onKeyDown={e => {
                            if (e.key !== 'Enter' || code.length !== 6) return;
                            if (step === 'driver_code') handleDriverVerifyCode();
                            else if (step === 'teacher_code') handleDriverVerifyCode(); // reuses same endpoint
                            else handleParentVerifyCode();
                        }}
                    />
                    <Button fullWidth variant="contained" disableElevation disabled={code.length !== 6 || loading}
                        onClick={step === 'parent_code' ? handleParentVerifyCode : handleDriverVerifyCode}
                        sx={primaryBtn}>
                        {loading ? <CircularProgress size={18} sx={{ color: NAV_BG }} /> : t('login.reg_code_verify')}
                    </Button>
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                        {step === 'parent_code' ? (
                            <Link component="button" type="button" underline="hover"
                                onClick={handleParentStart}
                                sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem', color: '#94a3b8', cursor: 'pointer', '&:hover': { color: AMBER_DARK } }}>
                                {t('login.reg_resend')}
                            </Link>
                        ) : resendCount < MAX_DRIVER_RESENDS ? (
                            <Link component="button" type="button" underline="hover"
                                onClick={step === 'teacher_code' ? handleTeacherResend : handleDriverResend}
                                sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem', color: '#94a3b8', cursor: 'pointer', '&:hover': { color: AMBER_DARK } }}>
                                {t('login.reg_resend')}
                            </Link>
                        ) : (
                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem', color: '#94a3b8' }}>
                                {t('login.reg_resend_limit')}
                            </Typography>
                        )}
                    </Box>
                </Box>
            )}

            {/* ── Parent: info ── */}
            {step === 'parent_info' && (
                <Box>
                    <Typography sx={labelSx}>{elUpperCase(t('login.reg_parent_first_name'))}</Typography>
                    <TextField
                        fullWidth autoFocus
                        placeholder={t('login.reg_parent_first_name_placeholder')}
                        value={firstName} onChange={e => setFirstName(e.target.value)}
                        size="small" sx={regInputSx}
                    />
                    <Typography sx={labelSx}>{elUpperCase(t('login.reg_parent_last_name'))}</Typography>
                    <TextField
                        fullWidth
                        placeholder={t('login.reg_parent_last_name_placeholder')}
                        value={lastName} onChange={e => setLastName(e.target.value)}
                        size="small" sx={regInputSx}
                    />
                    <Typography sx={labelSx}>{elUpperCase(t('login.reg_parent_email'))}</Typography>
                    <TextField
                        fullWidth type="email"
                        placeholder={t('login.reg_parent_email_placeholder')}
                        value={email} onChange={e => setEmail(e.target.value)}
                        size="small" sx={regInputSx}
                        onKeyDown={e => e.key === 'Enter' && firstName && lastName && email && handleParentStart()}
                    />
                    <Button fullWidth variant="contained" disableElevation disabled={!firstName || !lastName || !email || loading}
                        onClick={handleParentStart} sx={primaryBtn}>
                        {loading ? <CircularProgress size={18} sx={{ color: NAV_BG }} /> : t('login.reg_next')}
                    </Button>
                </Box>
            )}

            {/* ── Password (shared for driver + teacher + parent) ── */}
            {(step === 'driver_password' || step === 'teacher_password' || step === 'parent_password') && (
                <Box>
                    <Typography sx={labelSx}>{elUpperCase(t('login.reg_new_password'))}</Typography>
                    <TextField
                        fullWidth autoFocus type={showNew ? 'text' : 'password'}
                        placeholder={t('login.reg_new_password_placeholder')}
                        value={newPassword} onChange={e => setNewPassword(e.target.value)}
                        size="small" sx={regInputSx}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowNew(p => !p)} edge="end" size="small" tabIndex={-1} sx={{ color: '#94a3b8', mr: -0.5 }}>
                                        {showNew ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Typography sx={labelSx}>{elUpperCase(t('login.reg_confirm_password'))}</Typography>
                    <TextField
                        fullWidth type={showConfirm ? 'text' : 'password'}
                        placeholder={t('login.reg_confirm_password_placeholder')}
                        value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                        size="small" sx={{ ...regInputSx, mb: 3 }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowConfirm(p => !p)} edge="end" size="small" tabIndex={-1} sx={{ color: '#94a3b8', mr: -0.5 }}>
                                        {showConfirm ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        onKeyDown={e => e.key === 'Enter' && newPassword && confirmPassword && termsAccepted && (step === 'parent_password' ? handleParentSetPassword() : handleDriverSetPassword())}
                    />
                    <FormControlLabel
                        sx={{ mb: 2, alignItems: 'flex-start' }}
                        control={
                            <Checkbox
                                checked={termsAccepted}
                                onChange={e => setTermsAccepted(e.target.checked)}
                                size="small"
                                sx={{ color: '#94a3b8', '&.Mui-checked': { color: '#F59E0B' }, mt: '-2px' }}
                            />
                        }
                        label={
                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem', color: '#475569', lineHeight: 1.4 }}>
                                {t('login.reg_agree_terms')}{' '}
                                <Link href="/terms" target="_blank" underline="hover" sx={{ color: '#0f2d5e', fontWeight: 600 }}>
                                    {t('login.reg_terms_link')}
                                </Link>{' '}
                                {t('login.reg_and')}{' '}
                                <Link href="/privacy" target="_blank" underline="hover" sx={{ color: '#0f2d5e', fontWeight: 600 }}>
                                    {t('login.reg_privacy_link')}
                                </Link>
                            </Typography>
                        }
                    />
                    <Button fullWidth variant="contained" disableElevation disabled={!newPassword || !confirmPassword || !termsAccepted || loading}
                        onClick={step === 'parent_password' ? handleParentSetPassword : handleDriverSetPassword}
                        sx={primaryBtn}>
                        {loading ? <CircularProgress size={18} sx={{ color: NAV_BG }} /> : t('login.reg_set_password')}
                    </Button>
                </Box>
            )}
        </Box>
    );
};
// ─────────────────────────────────────────────────────────────────────────────

// ─── Forgot-password sub-component ───────────────────────────────────────────
const ForgotPassword = ({ backendUrl, onBack, t }) => {
    // step: 'email' | 'code' | 'password' | 'done'
    const [step, setStep] = useState('email');
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const fpInputSx = {
        mb: 2,
        '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            fontFamily: '"Outfit", sans-serif',
            background: 'white',
            '& fieldset': { borderColor: '#e2e8f0' },
            '&:hover fieldset': { borderColor: '#cbd5e1' },
            '&.Mui-focused fieldset': { borderColor: AMBER, borderWidth: 2 },
        },
        '& .MuiInputBase-input': {
            fontFamily: '"Outfit", sans-serif',
            fontSize: '0.95rem',
            color: NAV_BG,
            padding: '12px 14px',
        },
    };

    const labelSx = {
        fontFamily: '"Outfit", sans-serif',
        fontWeight: 600,
        fontSize: '0.8rem',
        color: '#64748b',
        mb: 0.75,
        letterSpacing: '0.04em',
    };

    const handleSendCode = async () => {
        if (!isValidEmail(email)) { setError(t('common.email_invalid')); return; }
        setError('');
        setLoading(true);
        try {
            const res = await fetch(backendUrl + 'api/admin/password-reset/request/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });
            if (res.ok) {
                setStep('code');
            } else {
                const data = await res.json().catch(() => ({}));
                if (data.error_code === 'email_not_found') {
                    setError(t('login.fp_error_email_not_found'));
                } else {
                    setError(t('login.fp_error_send'));
                }
            }
        } catch {
            setError(t('login.fp_error_send'));
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyCode = async () => {
        setError('');
        setLoading(true);
        try {
            const res = await fetch(backendUrl + 'api/admin/password-reset/verify/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, code }),
            });
            if (res.ok) {
                setStep('password');
            } else {
                setError(t('login.fp_error_invalid_code'));
            }
        } catch {
            setError(t('login.fp_error_invalid_code'));
        } finally {
            setLoading(false);
        }
    };

    const handleSetPassword = async () => {
        setError('');
        if (newPassword !== confirmPassword) { setError(t('login.fp_error_password_mismatch')); return; }
        if (newPassword.length < 6) { setError(t('login.fp_error_password_short')); return; }
        setLoading(true);
        try {
            const res = await fetch(backendUrl + 'api/admin/password-reset/confirm/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, code, new_password: newPassword }),
            });
            if (res.ok) {
                setStep('done');
            } else {
                setError(t('login.fp_error_invalid_code'));
            }
        } catch {
            setError(t('login.fp_error_invalid_code'));
        } finally {
            setLoading(false);
        }
    };

    const primaryBtn = {
        fontFamily: '"Outfit", sans-serif',
        fontWeight: 700,
        fontSize: '0.97rem',
        textTransform: 'none',
        borderRadius: '10px',
        py: 1.45,
        background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`,
        color: NAV_BG,
        boxShadow: `0 6px 24px rgba(245,158,11,0.35)`,
        '&:hover': { background: `linear-gradient(135deg, #fbbf24 0%, ${AMBER} 100%)`, transform: 'translateY(-1px)' },
        '&:active': { transform: 'translateY(0)' },
        '&.Mui-disabled': { background: '#e2e8f0', color: '#94a3b8', boxShadow: 'none' },
    };

    if (step === 'done') {
        return (
            <Box sx={{ textAlign: 'center', py: 2 }}>
                <CheckCircleOutline sx={{ fontSize: 52, color: '#10b981', mb: 2 }} />
                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '1.1rem', color: NAV_BG, mb: 1 }}>
                    {t('login.fp_success')}
                </Typography>
                <Button onClick={onBack} variant="text" startIcon={<ArrowBack />}
                    sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, color: AMBER_DARK, textTransform: 'none', mt: 2 }}>
                    {t('login.fp_back_to_login')}
                </Button>
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <IconButton onClick={step === 'email' ? onBack : () => setStep(step === 'code' ? 'email' : 'code')}
                    size="small" sx={{ color: '#64748b', p: 0.5 }}>
                    <ArrowBack sx={{ fontSize: 20 }} />
                </IconButton>
                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '1.15rem', color: NAV_BG }}>
                    {t('login.fp_title')}
                </Typography>
            </Box>

            {/* Step indicator */}
            <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                {['email', 'code', 'password'].map((s, i) => (
                    <Box key={s} sx={{
                        height: 3, flex: 1, borderRadius: 2,
                        background: ['email', 'code', 'password'].indexOf(step) >= i ? AMBER : '#e2e8f0',
                        transition: 'background 0.3s',
                    }} />
                ))}
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: '10px', fontFamily: '"Outfit", sans-serif', fontSize: '0.88rem' }}>
                    {error}
                </Alert>
            )}

            {step === 'email' && (
                <Box>
                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.88rem', color: '#64748b', mb: 2.5 }}>
                        {t('login.fp_subtitle')}
                    </Typography>
                    <Typography sx={labelSx}>{elUpperCase(t('login.fp_email_label'))}</Typography>
                    <TextField
                        fullWidth autoFocus type="email"
                        placeholder={t('login.fp_email_placeholder')}
                        value={email} onChange={e => setEmail(e.target.value)}
                        size="small" sx={fpInputSx}
                        onKeyDown={e => e.key === 'Enter' && email && handleSendCode()}
                    />
                    <Button fullWidth variant="contained" disableElevation disabled={!email || loading}
                        onClick={handleSendCode} sx={primaryBtn}>
                        {loading ? <CircularProgress size={18} sx={{ color: NAV_BG }} /> : t('login.fp_send_code')}
                    </Button>
                </Box>
            )}

            {step === 'code' && (
                <Box>
                    <Alert severity="info" sx={{ mb: 2.5, borderRadius: '10px', fontFamily: '"Outfit", sans-serif', fontSize: '0.88rem', background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', '& .MuiAlert-icon': { color: '#3b82f6' } }}>
                        {t('login.fp_code_sent')}
                    </Alert>
                    <Typography sx={labelSx}>{elUpperCase(t('login.fp_code_label'))}</Typography>
                    <TextField
                        fullWidth autoFocus inputProps={{ maxLength: 6, style: { fontSize: '1.4rem', textAlign: 'center', letterSpacing: '0.25em' } }}
                        placeholder={t('login.fp_code_placeholder')}
                        value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                        size="small" sx={{ ...fpInputSx, mb: 3, '& .MuiInputBase-input::placeholder': { letterSpacing: 'normal', fontSize: '0.9rem' } }}
                        onKeyDown={e => e.key === 'Enter' && code.length === 6 && handleVerifyCode()}
                    />
                    <Button fullWidth variant="contained" disableElevation disabled={code.length !== 6 || loading}
                        onClick={handleVerifyCode} sx={primaryBtn}>
                        {loading ? <CircularProgress size={18} sx={{ color: NAV_BG }} /> : t('login.fp_verify_code')}
                    </Button>
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                        <Link component="button" type="button" underline="hover"
                            onClick={() => { setStep('email'); setCode(''); setError(''); }}
                            sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem', color: '#94a3b8', cursor: 'pointer', '&:hover': { color: AMBER_DARK } }}>
                            {t('login.fp_resend')}
                        </Link>
                    </Box>
                </Box>
            )}

            {step === 'password' && (
                <Box>
                    <Typography sx={labelSx}>{elUpperCase(t('login.fp_new_password_label'))}</Typography>
                    <TextField
                        fullWidth autoFocus type={showNew ? 'text' : 'password'}
                        placeholder={t('login.fp_new_password_placeholder')}
                        value={newPassword} onChange={e => setNewPassword(e.target.value)}
                        size="small" sx={fpInputSx}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowNew(p => !p)} edge="end" size="small" tabIndex={-1} sx={{ color: '#94a3b8', mr: -0.5 }}>
                                        {showNew ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Typography sx={labelSx}>{elUpperCase(t('login.fp_confirm_password_label'))}</Typography>
                    <TextField
                        fullWidth type={showConfirm ? 'text' : 'password'}
                        placeholder={t('login.fp_confirm_password_placeholder')}
                        value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                        size="small" sx={{ ...fpInputSx, mb: 3 }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowConfirm(p => !p)} edge="end" size="small" tabIndex={-1} sx={{ color: '#94a3b8', mr: -0.5 }}>
                                        {showConfirm ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        onKeyDown={e => e.key === 'Enter' && newPassword && confirmPassword && handleSetPassword()}
                    />
                    <Button fullWidth variant="contained" disableElevation disabled={!newPassword || !confirmPassword || loading}
                        onClick={handleSetPassword} sx={primaryBtn}>
                        {loading ? <CircularProgress size={18} sx={{ color: NAV_BG }} /> : t('login.fp_save_password')}
                    </Button>
                </Box>
            )}
        </Box>
    );
};
// ─────────────────────────────────────────────────────────────────────────────

const LoginPage = () => {
    const { loginUser, loginError, loginLoading, backendUrl } = useContext(AuthContext);
    const [showPassword, setShowPassword] = useState(false);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [showRegister, setShowRegister] = useState(false);
    const { t } = useTranslation();

    const errorMessage = loginError
        ? t(`login.error_${loginError}`, { defaultValue: t('login.error_unknown') })
        : null;

    const inputSx = {
        mb: 2,
        '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            fontFamily: '"Outfit", sans-serif',
            background: 'white',
            '& fieldset': { borderColor: '#e2e8f0' },
            '&:hover fieldset': { borderColor: '#cbd5e1' },
            '&.Mui-focused fieldset': { borderColor: AMBER, borderWidth: 2 },
        },
        '& .MuiInputLabel-root': {
            fontFamily: '"Outfit", sans-serif',
            fontSize: '0.9rem',
            color: '#94a3b8',
        },
        '& .MuiInputLabel-root.Mui-focused': { color: AMBER_DARK },
        '& .MuiInputBase-input': {
            fontFamily: '"Outfit", sans-serif',
            fontSize: '0.95rem',
            color: NAV_BG,
            padding: '13px 14px',
        },
    };

    return (
        <Box sx={{
            display: 'flex',
            minHeight: '100vh',
            fontFamily: '"Outfit", sans-serif',
            '@keyframes fadeSlideUp': {
                from: { opacity: 0, transform: 'translateY(18px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
            },
            '@keyframes fadeIn': {
                from: { opacity: 0 },
                to: { opacity: 1 },
            },
        }}>

            {/* ─── LEFT PANEL ─── */}
            <Box sx={{
                display: { xs: 'none', lg: 'flex' },
                flex: '0 0 54%',
                flexDirection: 'column',
                background: `linear-gradient(160deg, ${NAV_BG} 0%, #0c2352 50%, #081a3e 100%)`,
                position: 'relative',
                overflow: 'hidden',
                p: '48px 52px',
            }}>
                {/* Subtle grid */}
                <Box sx={{
                    position: 'absolute', inset: 0, pointerEvents: 'none',
                    backgroundImage: `
                        linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)
                    `,
                    backgroundSize: '44px 44px',
                }} />

                {/* Top-right glow */}
                <Box sx={{
                    position: 'absolute', top: -120, right: -80, width: 420, height: 420,
                    borderRadius: '50%', pointerEvents: 'none',
                    background: `radial-gradient(circle, rgba(245,158,11,0.13) 0%, transparent 65%)`,
                }} />

                {/* Bottom-left glow */}
                <Box sx={{
                    position: 'absolute', bottom: -100, left: -60, width: 380, height: 380,
                    borderRadius: '50%', pointerEvents: 'none',
                    background: `radial-gradient(circle, rgba(22,53,113,0.9) 0%, transparent 70%)`,
                }} />

                {/* Logo */}
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5, zIndex: 1,
                    animation: 'fadeIn 0.6s ease both',
                }}>
                    <Box sx={{
                        width: 42, height: 42, borderRadius: '11px', flexShrink: 0,
                        background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: `0 6px 24px rgba(245,158,11,0.45)`,
                    }}>
                        <BusIcon size={22} />
                    </Box>
                    <Typography sx={{
                        fontFamily: '"Outfit", sans-serif',
                        fontWeight: 800, fontSize: '1.45rem', color: 'white', letterSpacing: '-0.01em',
                    }}>
                        SB<Box component="span" sx={{ color: AMBER }}>2</Box>
                    </Typography>
                </Box>

                {/* Tagline */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', zIndex: 1 }}>
                    <Typography sx={{
                        fontFamily: '"Outfit", sans-serif',
                        fontWeight: 800,
                        fontSize: 'clamp(1.9rem, 2.8vw, 2.8rem)',
                        color: 'white', lineHeight: 1.12, mb: 2,
                        animation: 'fadeSlideUp 0.7s ease 0.1s both',
                    }}>
                        {t('login.tagline_line1')}<br />
                        {t('login.tagline_line2')}<br />
                        <Box component="span" sx={{ color: AMBER }}>{t('login.tagline_accented')}</Box>
                    </Typography>
                    <Typography sx={{
                        fontFamily: '"Outfit", sans-serif',
                        fontSize: '0.97rem', color: 'rgba(255,255,255,0.45)',
                        maxWidth: 380, lineHeight: 1.75, mb: 5,
                        animation: 'fadeSlideUp 0.7s ease 0.2s both',
                    }}>
                        {t('login.description')}
                    </Typography>

                    {/* Route map */}
                    <Box sx={{ animation: 'fadeSlideUp 0.8s ease 0.3s both' }}>
                        <RouteMap />
                    </Box>

                    {/* Parent app on Google Play */}
                    <Link
                        href="https://play.google.com/store/apps/details?id=com.sbnextgen.parent&hl=en"
                        target="_blank" rel="noopener noreferrer" underline="none"
                        sx={{
                            mt: 3, alignSelf: 'flex-start',
                            display: 'flex', alignItems: 'center', gap: 1.5,
                            px: 2, py: 1.2, borderRadius: '12px',
                            border: '1px solid rgba(255,255,255,0.18)',
                            background: 'rgba(255,255,255,0.06)',
                            color: 'white', transition: 'all 0.2s ease',
                            animation: 'fadeSlideUp 0.8s ease 0.4s both',
                            '&:hover': { background: 'rgba(255,255,255,0.12)', borderColor: AMBER },
                        }}
                    >
                        <Shop sx={{ fontSize: 28, color: AMBER }} />
                        <Box>
                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.7rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.2 }}>
                                {t('login.parent_app_android')}
                            </Typography>
                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.95rem', fontWeight: 700, lineHeight: 1.2 }}>
                                {t('login.parent_app_google_play')}
                            </Typography>
                        </Box>
                    </Link>
                </Box>

            </Box>

            {/* ─── RIGHT PANEL ─── */}
            <Box sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f1f5f9',
                px: { xs: 3, sm: 6 },
                py: 5,
                position: 'relative',
                overflow: 'hidden',
            }}>
                {/* Subtle background circle */}
                <Box sx={{
                    position: 'absolute', top: -120, right: -120, width: 420, height: 420,
                    borderRadius: '50%', pointerEvents: 'none',
                    background: `radial-gradient(circle, rgba(245,158,11,0.05) 0%, transparent 70%)`,
                }} />

                {/* Mobile logo */}
                <Box sx={{
                    display: { xs: 'flex', lg: 'none' },
                    alignItems: 'center', gap: 1.5, mb: 5,
                    animation: 'fadeIn 0.5s ease both',
                }}>
                    <Box sx={{
                        width: 38, height: 38, borderRadius: '10px',
                        background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: `0 4px 16px rgba(245,158,11,0.35)`,
                    }}>
                        <BusIcon size={20} />
                    </Box>
                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 800, fontSize: '1.3rem', color: NAV_BG }}>
                        SB<Box component="span" sx={{ color: AMBER }}>2</Box>
                    </Typography>
                </Box>

                <Box sx={{
                    width: '100%', maxWidth: 380, zIndex: 1,
                    animation: 'fadeSlideUp 0.65s ease 0.1s both',
                }}>

                    {!showForgotPassword && !showRegister && (
                        <>
                            {/* Heading */}
                            <Box sx={{ mb: 4 }}>
                                <Typography sx={{
                                    fontFamily: '"Outfit", sans-serif',
                                    fontWeight: 700, fontSize: '2rem', color: NAV_BG, lineHeight: 1.1, mb: 0.75,
                                }}>
                                    {t('login.welcome_back')}
                                </Typography>
                                <Typography sx={{
                                    fontFamily: '"Outfit", sans-serif',
                                    fontSize: '0.92rem', color: '#94a3b8',
                                }}>
                                    {t('login.subtitle')}
                                </Typography>
                            </Box>

                            {/* Form card */}
                            <Box sx={{
                                background: 'white',
                                borderRadius: '18px',
                                border: '1px solid #e2e8f0',
                                boxShadow: '0 8px 40px rgba(15,45,94,0.08)',
                                p: '32px 28px 28px',
                            }}>
                                <form onSubmit={loginUser}>
                                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.8rem', color: '#64748b', mb: 0.75, letterSpacing: '0.04em' }}>
                                        {elUpperCase(t('login.username'))}
                                    </Typography>
                                    <TextField
                                        required fullWidth id="email" name="email" type="email"
                                        autoFocus autoComplete="email"
                                        placeholder={t('login.enter_username')}
                                        size="small"
                                        onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.closest('form').requestSubmit(); }}
                                        sx={{ ...inputSx, '& .MuiInputBase-input': { ...inputSx['& .MuiInputBase-input'], padding: '12px 14px' } }}
                                    />

                                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.8rem', color: '#64748b', mb: 0.75, letterSpacing: '0.04em' }}>
                                        {elUpperCase(t('login.password'))}
                                    </Typography>
                                    <TextField
                                        required fullWidth name="password" id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder={t('login.enter_password')}
                                        autoComplete="current-password" size="small"
                                        onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.closest('form').requestSubmit(); }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setShowPassword(p => !p)} edge="end" size="small" tabIndex={-1} sx={{ color: '#94a3b8', mr: -0.5 }}>
                                                        {showPassword ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{ ...inputSx, '& .MuiInputBase-input': { ...inputSx['& .MuiInputBase-input'], padding: '12px 14px' } }}
                                    />

                                    {errorMessage && (
                                        <Alert severity="error" sx={{ mb: 2, borderRadius: '10px', fontFamily: '"Outfit", sans-serif', fontSize: '0.88rem' }}>
                                            {errorMessage}
                                        </Alert>
                                    )}

                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -0.5, mb: 3 }}>
                                        <Link component="button" type="button" underline="hover"
                                            onClick={() => setShowForgotPassword(true)}
                                            sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem', color: '#94a3b8', cursor: 'pointer', '&:hover': { color: AMBER_DARK }, transition: 'color 0.2s' }}>
                                            {t('login.forgot_password')}
                                        </Link>
                                    </Box>

                                    <Button type="submit" fullWidth variant="contained" disableElevation disabled={loginLoading} sx={{
                                        fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '0.97rem',
                                        textTransform: 'none', borderRadius: '10px', py: 1.45,
                                        background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`,
                                        color: NAV_BG, boxShadow: `0 6px 24px rgba(245,158,11,0.35)`,
                                        letterSpacing: '0.01em', transition: 'all 0.2s ease',
                                        '&:hover': { background: `linear-gradient(135deg, #fbbf24 0%, ${AMBER} 100%)`, boxShadow: `0 8px 28px rgba(245,158,11,0.5)`, transform: 'translateY(-1px)' },
                                        '&:active': { transform: 'translateY(0)' },
                                        '&.Mui-disabled': loginLoading
                                            ? { background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`, color: NAV_BG, boxShadow: `0 6px 24px rgba(245,158,11,0.35)`, opacity: 0.85 }
                                            : { background: '#e2e8f0', color: '#94a3b8', boxShadow: 'none' },
                                    }}>
                                        {loginLoading
                                            ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <CircularProgress size={18} sx={{ color: NAV_BG }} />
                                                <span>{t('login.signing_in')}</span>
                                              </Box>
                                            : t('login.sign_in')}
                                    </Button>
                                </form>
                            </Box>

                            {/* Register link */}
                            <Box sx={{ textAlign: 'center', mt: 2.5 }}>
                                <Link component="button" type="button" underline="hover"
                                    onClick={() => setShowRegister(true)}
                                    sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.85rem', color: '#64748b', cursor: 'pointer', '&:hover': { color: AMBER_DARK }, fontWeight: 500 }}>
                                    {t('login.register_link')}
                                </Link>
                            </Box>
                        </>
                    )}

                    {showForgotPassword && (
                        <Box sx={{
                            background: 'white',
                            borderRadius: '18px',
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 8px 40px rgba(15,45,94,0.08)',
                            p: '32px 28px 28px',
                        }}>
                            <ForgotPassword
                                backendUrl={backendUrl}
                                onBack={() => setShowForgotPassword(false)}
                                t={t}
                            />
                        </Box>
                    )}

                    {showRegister && (
                        <Box sx={{
                            background: 'white',
                            borderRadius: '18px',
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 8px 40px rgba(15,45,94,0.08)',
                            p: '32px 28px 28px',
                        }}>
                            <Register
                                backendUrl={backendUrl}
                                onBack={() => setShowRegister(false)}
                                t={t}
                            />
                        </Box>
                    )}

                    {/* Footer note */}
                    <Typography sx={{
                        mt: 3, textAlign: 'center',
                        fontFamily: '"Outfit", sans-serif',
                        fontSize: '0.78rem', color: '#cbd5e1',
                    }}>
                        {t('login.footer_note')}
                    </Typography>

                    {/* Legal links */}
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
                        <Link href="/terms" underline="hover" sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.75rem', color: '#94a3b8', '&:hover': { color: AMBER_DARK } }}>
                            Terms & Conditions
                        </Link>
                        <Typography sx={{ fontSize: '0.75rem', color: '#cbd5e1' }}>·</Typography>
                        <Link href="/privacy" underline="hover" sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.75rem', color: '#94a3b8', '&:hover': { color: AMBER_DARK } }}>
                            Privacy Policy
                        </Link>
                    </Box>

                    {/* Language switcher */}
                    <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                        {['en', 'el'].map((lang) => (
                            <Button key={lang} size="small" onClick={() => {
                                i18n.changeLanguage(lang);
                                localStorage.setItem('sb_language', lang);
                            }} sx={{
                                minWidth: 0, px: 1.2, py: 0.3,
                                fontFamily: '"Outfit", sans-serif',
                                fontSize: '0.72rem', fontWeight: i18n.language === lang ? 700 : 400,
                                color: i18n.language === lang ? AMBER_DARK : '#94a3b8',
                                textTransform: 'uppercase',
                                borderRadius: '6px',
                                border: i18n.language === lang ? `1px solid ${AMBER}` : '1px solid transparent',
                                '&:hover': { color: AMBER_DARK, borderColor: AMBER, background: 'transparent' },
                                background: 'transparent',
                            }}>
                                {lang === 'en' ? 'EN' : 'ΕΛ'}
                            </Button>
                        ))}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default LoginPage;
